import type { ActivityPayload } from '../hydra';

/**
 * Build the slice the AI chat needs from a CMS preview draft.
 *
 * A draft has no database row, so the backend cannot look its content up the
 * way it does for a published activity. This carries what the prompt reads and
 * nothing else. The serialized result is checked before it is sent.
 *
 * Every question goes through, in page order. The hint and working-question
 * paths index this array by the `question_index` the client sends, so passing
 * only the "current" question would misalign the index.
 */
export type CmsPreviewChatContext = {
  topic: string;
  title: string;
  summary: string;
  questions: Array<{
    text: string;
    index_in_exercise: number;
    intro_lesson_text: string;
    type: ActivityPayload['exercise']['questions'][number]['type'];
    multiple_choice_answers: {
      correct_answer: string;
      distractors: string[];
    } | null;
    model_answer: string | null;
    answer_dql: string | null;
    initial_py: string | null;
    setup_sql: string | null;
    llm_initial_prompt: string | null;
  }>;
};

export function buildCmsPreviewChatContext(
  previewData: ActivityPayload
): CmsPreviewChatContext {
  const questions = previewData.exercise?.questions ?? [];

  return {
    topic: previewData.content_title ?? previewData.title,
    title: previewData.title,
    summary: previewData.summary ?? '',
    questions: questions.map((q) => ({
      text: q.text,
      index_in_exercise: q.index_in_exercise,
      intro_lesson_text: q.intro_lesson_text,
      type: q.type,
      multiple_choice_answers: q.multiple_choice_answers,
      model_answer: q.model_answer ?? null,
      answer_dql: q.answer_dql ?? null,
      initial_py: q.initial_py ?? null,
      setup_sql: q.setup_sql ?? null,
      llm_initial_prompt: q.llm_initial_prompt ?? null,
    })),
  };
}
